// 静态导入 SDK 稳定的具名导出。`emitCustomEvent` 自 alpha.5 起加入，
// 这里不静态具名导入，而是通过下方 eager 动态 import 访问，规避 SDK 回退到
// 旧版本时的静态缺失导出告警，并在缺失/异常时安全降级。
import { init, bind, destroy } from '@cathay/web-recorder';

const isLocalRecorder = process.env.VUE_APP_LOCAL_RECORDER === 'true';
const STORAGE_KEY = isLocalRecorder
  ? 'cathay:recorder:local-session'
  : 'cathay:recorder:session';
let initialized = false;

const isProd = process.env.NODE_ENV === 'production';

const RECORDER_CONFIGS = {
  test: {
    project: 'insurance-car-h5',
    key: 'srk_2lfFCgsGoT4QSGkdJ29iR7HEfkcyi1b3',
  },
  production: {
    project: 'insurance-car-h5',
    key: 'srk_AMTl9ixxuOoPPQwvQiaPUzglpnwsXTAq',
  },
};

const RECORDER_CONFIG = {
  ...(isProd ? RECORDER_CONFIGS.production : RECORDER_CONFIGS.test),
  uploadUrl: isLocalRecorder ? '/local-recorder' : '/aas/api/session-recording',
};

/**
 * 输入脱敏：手机号、姓名、证件号、银行卡号
 */
function maskInput(text, element) {
  const field = [
    element.getAttribute('name'),
    element.getAttribute('id'),
    element.getAttribute('autocomplete'),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  // 手机号
  if (/phone|mobile|tel/.test(field)) {
    return text.replace(/(\d{3})\d+(\d{4})/, '$1****$2');
  }

  // 证件号、银行卡
  if (/idcard|cert|identity|bank|card/.test(field)) {
    return '*'.repeat(text.length);
  }

  // 姓名
  if (/name/.test(field)) {
    return text.length > 0 ? `${text.slice(0, 1)}**` : '';
  }

  return text;
}

/**
 * 初始化录制 SDK，整个应用只调用一次。
 * 进入页面即开始录制。
 */
export function initRecorder() {
  if (initialized) return;

  try {
    void init({
      ...RECORDER_CONFIG,
      storageKey: STORAGE_KEY,
      queryKey: isLocalRecorder ? 'localRecordNo' : 'cathayRecordNo',
      recordCanvas: true,
      maskInputFn: maskInput,
      sampling: {
        // 全局 canvas 录制固定 20 FPS / JPEG 0.2，保证 PDF 与回退兼容。
        // 签名笔画已通过自定义事件 `signature-point` 单独记录，不依赖 canvas 采样。
        canvas: 10,
      },
      dataURLOptions: {
        type: 'image/jpeg',
        quality: 0.2,
      },
      onError: function (error) {
        window.console.warn('[web-recorder]', error);
      },
    });
    initialized = true;
  } catch (e) {
    // 录制失败不阻塞业务
    window.console.warn('[recorder] init failed:', e);
  }
}

/**
 * 绑定用户或业务信息。
 * 新版 SDK 支持初始化未完成时自动排队，直接调用即可。
 *
 * @param {Object} data - { userId?, bizNo?, bizType?, extJson? }
 */
export function bindRecord(data) {
  try {
    bind(data);
  } catch (e) {
    // 绑定失败不阻塞业务
    window.console.warn('[recorder] bind failed:', e);
  }
}

/**
 * 销毁当前录制会话。
 * 用于登出或身份切换时调用。
 */
export async function resetRecorder() {
  try {
    await destroy();
  } catch (e) {
    window.console.warn('[recorder] destroy failed:', e);
  }
  initialized = false;
}

// ---- 自定义事件 -----------------------------------------------------------
//
// `emitCustomEvent(tag, payload)` 自 alpha.5 起是 SDK 的具名导出。这里通过
// eager 动态 import 在模块加载时预取 SDK 并缓存具名导出，运行时封装为同步
// 业务包装函数。本地录制与正常上传模式都加载该 API；任何运行时异常（包括
// SDK 回退到不含该导出的旧版本）都被吞掉并最多告警一次，绝不影响业务 UI
// （例如签名流程的 pointer 事件）。

let cachedEmitCustomEvent = null; // null = 尚未解析或不可用；function = 可用
let emitApiResolved = false; // 异步加载是否完成
let emitMissingWarned = false; // 缺失/异常最多告警一次，避免每个 pointer 事件刷屏

// eager 模式避免拆出额外异步 chunk；运行时访问规避静态导出检查。
import(/* webpackMode: 'eager' */ '@cathay/web-recorder')
  .then(mod => {
    if (mod && typeof mod.emitCustomEvent === 'function') {
      cachedEmitCustomEvent = mod.emitCustomEvent;
    }
  })
  .catch(e => {
    window.console.warn('[recorder] load web-recorder failed:', e);
  })
  .finally(() => {
    emitApiResolved = true;
  });

/**
 * 同步业务包装：向当前录制会话注入一条自定义事件。
 *
 * 本地录制与正常上传模式都生效。SDK 缺失该 API 时静默跳过，缺失最多告警
 * 一次。任何运行时异常都被吞掉并最多告警一次，绝不影响业务 UI（例如签名
 * 流程的 pointer 事件）。
 *
 * @param {string} tag - 自定义事件标签
 * @param {*} [payload] - 任意可序列化数据
 */
export function emitRecorderCustomEvent(tag, payload) {
  const fn = cachedEmitCustomEvent;
  if (typeof fn !== 'function') {
    if (emitApiResolved && !emitMissingWarned) {
      emitMissingWarned = true;
      window.console.warn(
        '[recorder] emitCustomEvent is not available in this SDK build',
      );
    }
    return;
  }

  try {
    fn(tag, payload);
  } catch (e) {
    if (!emitMissingWarned) {
      emitMissingWarned = true;
      window.console.warn('[recorder] emitCustomEvent failed:', e);
    }
  }
}
